import { generatePath } from 'react-router';

import { Link } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { Text } from 'Components/Text/Text';
import { UserDefinedReference } from 'Components/UserDefinedReference/UserDefinedReference';
import { TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, UCRM_BUSINESSES_AND_USE_CASES_LISTING } from 'Config/PathTemplates';
import { ISSUES_EXCEPTIONS, TPRM, UCRM } from 'Config/Paths';
import { TPRM_THIRD_PARTY_MANAGER, UCRM_BUSINESS_MANAGER } from 'Config/Tooltips';
import { SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID } from 'Helpers/URLBuilder/URLBuilder';
import { UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { countOpenExceptions } from 'Models/Exceptions';
import { countOpenIssues } from 'Models/Issues';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { ThirdPartyResponseWithServices } from 'Models/TPRM';
import { BusinessResponseWithUseCases } from 'Models/Ucrm';

import styles from './OrganizationSummary.module.css';

interface OrganizationSummaryProps {
    /** Determines whether `organization` is a TPRM third party or a UCRM business. */
    module: DueDiligenceModule;
    organization: ThirdPartyResponseWithServices | BusinessResponseWithUseCases;
}

/**
 * Renders details about a TPRM third party or a UCRM business that roughly match the details shown for it on the module's listing page.
 * Used on the Issue Details and Exception Details pages.
 */
export const OrganizationSummary = (props: OrganizationSummaryProps) => {
    const cachedData = useCachedData();

    const isUcrm = props.module === ProductModule.UCRM;
    const numberOfAssessmentSubjects = isUcrm ? (props.organization as BusinessResponseWithUseCases).use_cases.length : (props.organization as ThirdPartyResponseWithServices).services.length;
    const issuesExceptionsPath = `/${isUcrm ? UCRM : TPRM}/${ISSUES_EXCEPTIONS}`;
    const subjectsLabel = isUcrm ? 'Use Cases' : 'Services';

    const numberOfOpenIssues = countOpenIssues(props.organization.issues);
    const numberOfOpenExceptions = countOpenExceptions(props.organization.exceptions);

    return (
        <div className={styles.detailsContainer}>
            <div className={styles.detailsGridSection}>
                <div className={styles.detailsGridColumn}>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName={isUcrm ? 'Business Manager' : 'Third-Party Manager'} tooltipText={isUcrm ? UCRM_BUSINESS_MANAGER : TPRM_THIRD_PARTY_MANAGER} />
                        <Text noStyles>{getUserNameFromSubject(props.organization.vendor_manager_user_id, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}</Text>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Issues" />
                        <div>
                            <Link size="sm" to={{ pathname: issuesExceptionsPath, search: `${SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID}=${props.organization.id}`, hash: '#issues' }}>{`Open Issues: ${numberOfOpenIssues}`}</Link>
                        </div>
                    </div>
                </div>
                <div className={styles.detailsGridColumn}>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName={subjectsLabel} />
                        <Link size="sm" to={{ pathname: generatePath(isUcrm ? UCRM_BUSINESSES_AND_USE_CASES_LISTING : TPRM_THIRD_PARTIES_AND_SERVICES_LISTING) }}>
                            {`${subjectsLabel}: ${numberOfAssessmentSubjects}`}
                        </Link>
                    </div>
                    <div className={styles.labelAndValueContainer}>
                        <FieldLabel fieldName="Exceptions" />
                        <div>
                            <Link size="sm" to={{ pathname: issuesExceptionsPath, search: `${SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID}=${props.organization.id}`, hash: '#exceptions' }}>{`Open Exceptions: ${numberOfOpenExceptions}`}</Link>
                        </div>
                    </div>
                </div>
            </div>
            {!isUcrm && (
                <div className={styles.labelAndValueContainer}>
                    <FieldLabel fieldName="Website" />
                    <UserDefinedReference reference={(props.organization as ThirdPartyResponseWithServices).website} />
                </div>
            )}
        </div>
    );
};
